import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { isSuccessfulStudentCategory } from "../studentApplication";

const DIRECT_BENEFICIARY_STATUS_ID = 900;
const PENDING_LOTTERY_STATUS_ID = 905;

const buildInitialDecision = (child) => ({
  route:
    child?.decisionStatusId === DIRECT_BENEFICIARY_STATUS_ID
      ? "direct"
      : child?.decisionStatusId === PENDING_LOTTERY_STATUS_ID
        ? "lottery"
        : child?.preApprovalGuidance?.suggestedReasonId
          ? "direct"
          : "lottery",
  reasonId:
    child?.directApprovalReason?.id ??
    child?.preApprovalGuidance?.suggestedReasonId ??
    "",
});

const PreApprovalDialog = ({
  open,
  children = [],
  reasons = [],
  onClose,
  onConfirm,
}) => {
  const successfulChildren = useMemo(
    () => children.filter((child) => isSuccessfulStudentCategory(child?.category)),
    [children]
  );
  const [decisions, setDecisions] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    setDecisions(
      Object.fromEntries(
        successfulChildren.map((child) => [child.id, buildInitialDecision(child)])
      )
    );
    setSubmitting(false);
  }, [open, successfulChildren]);

  const updateDecision = (childId, changes) => {
    setDecisions((current) => ({
      ...current,
      [childId]: {
        ...current[childId],
        ...changes,
      },
    }));
  };

  const hasMissingDecision = successfulChildren.some((child) => {
    const decision = decisions[child.id];
    return (
      !decision?.route ||
      (decision.route === "direct" && !decision.reasonId)
    );
  });

  const handleConfirm = async () => {
    if (hasMissingDecision || submitting) return;

    const memberDecisions = successfulChildren.map((child) => {
      const decision = decisions[child.id];
      const isDirect = decision.route === "direct";

      return {
        memberId: child.id,
        decisionStatusId: isDirect
          ? DIRECT_BENEFICIARY_STATUS_ID
          : PENDING_LOTTERY_STATUS_ID,
        directApprovalReasonId: isDirect ? Number(decision.reasonId) : null,
      };
    });

    try {
      setSubmitting(true);
      await onConfirm(memberDecisions);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Προ-Έγκριση Αίτησης</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ mb: 2 }}>
          Επιλέξτε για κάθε επιτυχόν τέκνο αν θα εξαιρεθεί από την κλήρωση ή θα συμμετάσχει σε αυτή.
        </Typography>

        <Stack spacing={2}>
          {successfulChildren.map((child) => {
            const guidance = child?.preApprovalGuidance;
            const decision = decisions[child.id] ?? buildInitialDecision(child);
            const hasDirectApprovalGuidance =
              guidance?.isDisabled ||
              guidance?.isLargeFamily ||
              guidance?.isThreeChildFamily ||
              guidance?.isOrphan ||
              guidance?.hasFosterCareBenefitAttachment;

            return (
              <Paper key={child.id} variant="outlined" sx={{ overflow: "hidden" }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: "grey.50",
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography color="primary.main" fontWeight={600}>
                      {child?.lastName} {child?.firstName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ΑΜΚΑ: {child?.amka}
                    </Typography>
                  </Box>
                  <Chip label={child?.category?.name} color="primary" variant="outlined" />
                </Box>

                <Stack spacing={1.5} sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Ενδείξεις ελέγχου
                  </Typography>
                  {guidance?.isDisabled && (
                    <Alert severity="info" icon={false}>
                      Το τέκνο έχει δηλωθεί ως Επιτυχών/ούσα Πανελλαδικών + ΑμεΑ.
                    </Alert>
                  )}
                  {guidance?.isLargeFamily && (
                    <Alert severity="info" icon={false}>
                      Η αίτηση περιλαμβάνει 4 ή περισσότερα προστατευόμενα τέκνα — ένδειξη πολύτεκνης οικογένειας.
                    </Alert>
                  )}
                  {guidance?.isThreeChildFamily && (
                    <Alert severity="info" icon={false}>
                      Η αίτηση περιλαμβάνει 3 προστατευόμενα τέκνα — ένδειξη τρίτεκνης οικογένειας.
                    </Alert>
                  )}
                  {guidance?.isOrphan && (
                    <Alert severity="info" icon={false}>
                      Το τέκνο έχει χαρακτηριστεί ως ορφανό στη λίστα δικαιούχων της περιόδου.
                    </Alert>
                  )}
                  {guidance?.hasFosterCareBenefitAttachment && (
                    <Alert severity="info" icon={false}>
                      Έχει επισυναφθεί απόφαση οικονομικής ενίσχυσης αναδοχής.
                    </Alert>
                  )}
                  {!hasDirectApprovalGuidance && (
                    <Alert severity="info" icon={false}>
                      Δεν προκύπτει ένδειξη εξαίρεσης από την κλήρωση.
                    </Alert>
                  )}

                  <FormControl required>
                    <FormLabel>Απόφαση για το τέκνο</FormLabel>
                    <RadioGroup
                      row
                      value={decision.route}
                      onChange={(event) =>
                        updateDecision(child.id, { route: event.target.value })
                      }
                    >
                      <FormControlLabel
                        value="direct"
                        control={<Radio />}
                        label="Χωρίς κλήρωση"
                      />
                      <FormControlLabel
                        value="lottery"
                        control={<Radio />}
                        label="Προς κλήρωση"
                      />
                    </RadioGroup>
                  </FormControl>

                  {decision.route === "direct" && (
                    <FormControl required fullWidth>
                      <FormLabel sx={{ mb: 0.75 }}>Αιτία απευθείας πληρωμής</FormLabel>
                      <Select
                        value={decision.reasonId}
                        displayEmpty
                        onChange={(event) =>
                          updateDecision(child.id, { reasonId: event.target.value })
                        }
                      >
                        <MenuItem value="" disabled>
                          Επιλέξτε αιτία
                        </MenuItem>
                        {reasons.map((reason) => (
                          <MenuItem key={reason.id} value={reason.id}>
                            {reason.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} color="error" variant="contained">
          Ακύρωση
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={hasMissingDecision || submitting || successfulChildren.length === 0}
          color="success"
          variant="contained"
        >
          Επιβεβαίωση Προ-Έγκρισης
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreApprovalDialog;
